/**
 * Self buff/debuff bar (#139) — WoW-style top-right tracker of the VIEWER'S OWN
 * auras, split into a buffs row and a debuffs row, each chip showing icon +
 * duration countdown + stack count.
 *
 * DISTINCT from the nameplate/frame aura rows (#137/#138): those show a curated
 * "major" subset on OTHER units; this shows (nearly) ALL of your own auras — the
 * self-status HUD. It reads the OWN entity's already-wired `auras` list
 * (`WireEntity.auras`, resolved via `ownSeat`) — NO new sim/protocol/wire data.
 *
 * #922: the bar is no longer read-only — RIGHT-CLICKING a BUFF chip sends the
 * `cancelAura` sim input for that chip's def ("I want this buff off"). Debuff
 * chips are inert: the handler is only ever passed to the buff row, and the sim
 * gates on `polarity` independently, so a debuff can never be clicked off.
 * Everything else here remains cosmetic.
 *
 * Behaviour:
 *   • Split buff vs debuff via the WIRE-CARRIED `polarity` (#168) — the server
 *     authors each aura's buff/debuff class on the def; the client reads it off
 *     `WireAura.polarity` (no local mirror table, no "unknown → buff" default).
 *   • Shows literally EVERY own aura at v1 (unlike the #137 major-subset filter
 *     on other units) — a `hidden`/internal-carrier exclusion is a reserved hook
 *     on `auraIcons.ts`, not built here (no internal carrier aura exists yet).
 *   • Sort each group by soonest-expiring-first; permanent (no-expiry) auras
 *     last. A total, deterministic order (see {@link compareAurasForBar}).
 *   • Anchored top-right (fixed default); all layout knobs live on
 *     {@link SelfAuraBarStyle} for later per-user customization (#48 pattern).
 *
 * data-* contract:
 *   • data-testid="self-aura-bar" — always present (empty when no auras)
 *   • data-buff-count / data-debuff-count — group sizes
 *   • child rows data-testid="self-buff-row" / "self-debuff-row"
 *   • each chip is an <AuraChip> (data-testid "self-aura-chip")
 */

import type { CSSProperties, ReactElement } from "react";
import type { WireAura } from "@pit/protocol";
import { useStore } from "../store/context.js";
import { useWsClient } from "../net/WsClientProvider.js";
import { ownEntity } from "./hud-util.js";
import { DEFAULT_AURA_CHIP_STYLE, type AuraChipStyle } from "./AuraChip.js";
import { AuraStrip } from "./AuraStrip.js";
import {
  compareAurasByExpiry,
  sortAuras,
  splitAurasByPolarity,
} from "./auraFilter.js";

/** Sim tick rate — matches MatchScreen / Countdown / SoundController. */
const TICK_RATE = 30;

/** Layout treatment for the self-aura bar. Every knob is data-driven. */
export interface SelfAuraBarStyle {
  /** Distance from the top viewport edge, px. */
  readonly topPx: number;
  /** Distance from the right viewport edge, px. */
  readonly rightPx: number;
  /** Gap between the buffs row and the debuffs row, px. */
  readonly rowGapPx: number;
  /** Gap between chips within a row, px. */
  readonly chipGapPx: number;
  /** Max width of a wrapping row, px (chips wrap to a grid past this). */
  readonly rowMaxWidthPx: number;
  /** Per-chip visual style. */
  readonly chip: AuraChipStyle;
}

/** Default treatment: fixed top-right, buffs above debuffs. */
export const DEFAULT_SELF_AURA_BAR_STYLE: SelfAuraBarStyle = {
  topPx: 16,
  rightPx: 16,
  rowGapPx: 8,
  chipGapPx: 4,
  rowMaxWidthPx: 336,
  chip: DEFAULT_AURA_CHIP_STYLE,
};

/**
 * Deterministic ordering for the self-bar: soonest-expiring first, permanent
 * (no-expiry) auras last. Ties broken by def id, then source seat, then applied
 * tick — a TOTAL order, so render order never wobbles between equal auras.
 */
export const compareAurasForBar = compareAurasByExpiry;

/**
 * Partition the viewer's auras into sorted buff / debuff groups. Pure — the
 * reducer of the whole bar, exposed so the component test drives split + sort
 * directly.
 */
export function selfBarAuras(
  auras: readonly WireAura[],
  now: number,
): { readonly buffs: readonly WireAura[]; readonly debuffs: readonly WireAura[] } {
  const { buffs, debuffs } = splitAurasByPolarity(auras);
  return {
    buffs: sortAuras(buffs, now, true),
    debuffs: sortAuras(debuffs, now, true),
  };
}

export function SelfAuraBar({
  style = DEFAULT_SELF_AURA_BAR_STYLE,
}: {
  readonly style?: SelfAuraBarStyle;
} = {}): ReactElement {
  const { state } = useStore();
  const client = useWsClient();
  const me = ownEntity(state.hudSnapshot, state.ownSeat);
  const now = state.hudSnapshot?.tick ?? 0;
  const { buffs, debuffs } = selfBarAuras(me?.auras ?? [], now);

  const container: CSSProperties = {
    position: "absolute",
    top: style.topPx,
    right: style.rightPx,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: style.rowGapPx,
    pointerEvents: "none",
  };

  const rowStyle: CSSProperties = {
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: style.chipGapPx,
    maxWidth: style.rowMaxWidthPx,
  };

  return (
    <div
      data-testid="self-aura-bar"
      data-buff-count={String(buffs.length)}
      data-debuff-count={String(debuffs.length)}
      style={container}
    >
      <AuraStrip
        testId="self-buff-row"
        chipTestId="self-aura-chip"
        auras={buffs}
        now={now}
        tickRate={TICK_RATE}
        chipStyle={style.chip}
        style={rowStyle}
        // #922: right-click a BUFF chip to cancel it. Passed ONLY to the buff
        // row — the debuff row gets no handler at all, so debuff chips are
        // inert by CONSTRUCTION rather than by a runtime polarity re-check
        // (the sim gate is authoritative regardless; this is the client half
        // of "you cannot click a stun off yourself").
        onCancelAura={(def) => {
          client.sendInput({ kind: "cancelAura", def });
        }}
      />
      <AuraStrip
        testId="self-debuff-row"
        chipTestId="self-aura-chip"
        auras={debuffs}
        now={now}
        tickRate={TICK_RATE}
        chipStyle={style.chip}
        style={rowStyle}
      />
    </div>
  );
}
